import { FC, useEffect, useState } from 'react';
import { createColumnHelper, ColumnDef } from '@tanstack/react-table';
import {
  InlineLoading,
  InlineNotification,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tag,
} from '@carbon/react';
import { DataTable } from 'components/data-table';
import { formatNumber } from 'utils/number';
import { fetchSlotDetail } from './atoms';
import {
  CATEGORY_LABELS,
  acres,
  count,
  interest,
  interestPercent,
  share,
  coverageSentence,
  OVERSIZED_GEOMETRY_MULTIPLE,
} from './utils';
import type {
  CategoryVerdict,
  OverAllocatedSlot,
  OverAllocationCategory,
  SlotContribution,
  SlotDetailResponse,
} from './types';
import styles from './page.module.scss';

const columnHelper = createColumnHelper<SlotContribution>();

/**
 * The evidence count behind each mechanism, so a tag carries the weight of the
 * finding and not just its name. The labels themselves live in utils, shared
 * with the list so a slot is named identically in both places.
 */
const CATEGORY_EVIDENCE: Record<
  OverAllocationCategory,
  (v: CategoryVerdict) => string
> = {
  understated_gross: (v) => `${count(v.understatedGrossRows)} rows`,
  duplicate_parcel: (v) => `${count(v.overClaimedParcels)} parcels`,
};

const columns = [
  columnHelper.accessor('subtractName', {
    id: 'subtractName',
    header: 'Subtract',
    size: 150,
  }),
  columnHelper.accessor('tractName', {
    id: 'tractName',
    header: 'CRM Tract',
    size: 130,
  }),
  columnHelper.accessor('accountName', {
    id: 'accountName',
    header: 'CRM Account',
    size: 220,
  }),
  columnHelper.accessor('uatName', {
    id: 'uatName',
    header: 'UAT',
    size: 150,
  }),
  columnHelper.accessor('estate', {
    id: 'estate',
    header: 'Estate',
    size: 100,
  }),
  columnHelper.accessor('netAcreageUnits', {
    id: 'netAcreageUnits',
    header: 'Units',
    size: 80,
  }),
  columnHelper.accessor('nri', {
    id: 'nri',
    header: 'Allocated NRI',
    size: 140,
    cell: (info) => interest(info.getValue()),
  }),
  columnHelper.accessor('workingInterest', {
    id: 'workingInterest',
    header: 'Allocated WI',
    size: 140,
    cell: (info) => interest(info.getValue()),
  }),
  columnHelper.accessor('bosNri', {
    id: 'bosNri',
    header: 'BOS NRI',
    size: 130,
    cell: (info) => interest(info.getValue()),
  }),
  columnHelper.accessor('bosWorkingInterest', {
    id: 'bosWorkingInterest',
    header: 'BOS WI',
    size: 130,
    cell: (info) => interest(info.getValue()),
  }),
  columnHelper.accessor('nma', {
    id: 'nma',
    header: 'NMA',
    size: 110,
    cell: (info) => acres(info.getValue()),
  }),
  columnHelper.accessor('nra', {
    id: 'nra',
    header: 'NRA',
    size: 110,
    cell: (info) => acres(info.getValue()),
  }),
  columnHelper.accessor('grossAcres', {
    id: 'grossAcres',
    header: 'Gross Acres',
    size: 130,
    cell: (info) => acres(info.getValue()),
  }),
  // The chain that produced this row's interest, left-to-right:
  //   tractNri x (creditedAcres / unitAcres) = allocated NRI
  // with geometry vs recorded acreage showing whether the weight was earned.
  columnHelper.accessor('tractNri', {
    id: 'tractNri',
    header: 'Tract NRI (input)',
    size: 150,
    cell: (info) => interest(info.getValue()),
  }),
  columnHelper.accessor('recordedAcres', {
    id: 'recordedAcres',
    header: 'Recorded Acres',
    size: 140,
    cell: (info) => acres(info.getValue()),
  }),
  columnHelper.accessor('geometryAcres', {
    id: 'geometryAcres',
    header: 'Geometry Acres',
    size: 140,
    cell: (info) => acres(info.getValue()),
  }),
  columnHelper.accessor('geometryVsRecorded', {
    id: 'geometryVsRecorded',
    header: 'Geometry / Recorded',
    size: 170,
    cell: (info) => {
      const ratio = info.getValue();
      if (ratio === null || ratio === undefined) return '--';
      const label = `${formatNumber(ratio, 0, 2, 'decimal', true)}x`;
      // A polygon materially larger than the acreage it represents is the
      // signal worth chasing — it earns the row an outsized share of the unit.
      return ratio > OVERSIZED_GEOMETRY_MULTIPLE ? (
        <Tag type="red" size="sm">
          {label}
        </Tag>
      ) : (
        label
      );
    },
  }),
  columnHelper.accessor('coverageUsed', {
    id: 'coverageUsed',
    header: 'Share Used',
    size: 130,
    cell: (info) => share(info.getValue()),
  }),
  columnHelper.accessor('trueShare', {
    id: 'trueShare',
    header: 'Share Expected',
    size: 140,
    cell: (info) => share(info.getValue()),
  }),
  columnHelper.accessor('creditedAcres', {
    id: 'creditedAcres',
    header: 'Credited Acres',
    size: 140,
    cell: (info) => acres(info.getValue()),
  }),
  // Which physical parcel this record sits on, and whether that parcel is
  // claimed past 100% between the records sharing it. Matching the short hash
  // across rows shows at a glance which records describe the same ground.
  columnHelper.accessor('parcelKey', {
    id: 'parcelKey',
    header: 'Parcel',
    size: 190,
    cell: (info) => {
      const key = info.getValue();
      if (!key) return '--';
      const { parcelRecordCount, parcelOverClaimed } = info.row.original;
      return (
        <>
          <code>{key.slice(0, 8)}</code>
          {parcelOverClaimed ? (
            <Tag type="red" size="sm">
              {`${count(parcelRecordCount)} recs`}
            </Tag>
          ) : (
            (parcelRecordCount ?? 0) > 1 && (
              <Tag type="gray" size="sm">
                {`${count(parcelRecordCount)} recs`}
              </Tag>
            )
          )}
        </>
      );
    },
  }),
  columnHelper.accessor('sharedGrossAcresCount', {
    id: 'sharedGrossAcresCount',
    header: 'Same Gross',
    size: 120,
    cell: (info) => {
      const shared = info.getValue();
      if (shared < 2) return '--';
      return (
        <Tag type="magenta" size="sm">
          {`${shared} subtracts`}
        </Tag>
      );
    },
  }),
];

const TABLE_CONFIG = {
  sorting: true,
  globalSearch: true,
  columnFilters: false,
  columnResizing: true,
  columnReordering: false,
  columnPinning: false,
  columnVisibility: true,
} as const;

/**
 * Expanded-row detail for one over-allocated slot: the estate breakdown that
 * localises the excess, then every calculation feeding the roll-up alongside
 * the Bundle of Sticks values it was allocated down from.
 *
 * @param {object} props - The list row to detail (either grain).
 * @return {JSX.Element} The estate breakdown and contributing-subtract tables.
 */
export const SlotDetail: FC<{ slot: OverAllocatedSlot }> = ({ slot }) => {
  const [detail, setDetail] = useState<SlotDetailResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setDetail(null);
    setError(null);

    fetchSlotDetail(slot)
      .then((response) => {
        if (!cancelled) setDetail(response);
      })
      .catch((e: unknown) => {
        if (!cancelled) {
          setError(
            e instanceof Error ? e.message : 'Failed to load slot detail'
          );
        }
      });

    return () => {
      cancelled = true;
    };
  }, [slot]);

  if (error) {
    return (
      <InlineNotification
        kind="error"
        title="Could not load slot detail"
        subtitle={error}
        hideCloseButton
      />
    );
  }

  if (!detail) {
    return <InlineLoading description="Loading slot detail…" />;
  }

  return (
    <div className={styles.detail}>
      {/* The one line that makes an over-allocated slot legible: how much
    participation was handed out against how much land the unit has. */}
      {detail.coverage.unitAcres !== null && (
        <InlineNotification
          kind={
            (detail.coverage.creditedMultiple ?? 0) > 1.05 ? 'warning' : 'info'
          }
          title="Coverage"
          subtitle={coverageSentence(
            detail.coverage,
            detail.contributions.length
          )}
          hideCloseButton
          lowContrast
        />
      )}

      {/* Which known mechanism the evidence points at. Both can apply, and
    neither means unexplained rather than sound — so an empty verdict says so
    outright instead of rendering nothing. */}
      <div className={styles.detailSectionTitle}>Category</div>
      <div>
        {detail.categories.keys.length === 0 ? (
          <Tag type="cool-gray" size="sm">
            Unexplained — neither known mechanism detected
          </Tag>
        ) : (
          detail.categories.keys.map((key) => (
            <Tag key={key} type={CATEGORY_LABELS[key].tag} size="sm">
              {`${CATEGORY_LABELS[key].long} — ${CATEGORY_EVIDENCE[key](
                detail.categories
              )}`}
            </Tag>
          ))
        )}
      </div>

      {/* Records sharing a polygon describe the same ground. Sharing alone is
    ordinary co-ownership; the parcels claimed past 100% are the duplicates and
    superseded leases, so those sort to the top and carry the tag.

    OVER-CLAIMED IS JUDGED PER ACCOUNT, so the account columns carry the figures
    the tag is actually based on. The all-owners sums are context and nothing
    more — three owners summing past 100% between them is the ordinary case a
    conveyance we never ingested explains, and reading the tag against those
    numbers is what makes this table look like it sums across parties. */}
      {detail.parcels.length > 0 && (
        <>
          <div className={styles.detailSectionTitle}>
            {`Parcel coverage (${
              detail.parcels.filter((p) => p.overClaimed).length
            } of ${detail.parcels.length} claimed past 100%)`}
          </div>
          <Table size="sm" className={styles.estateTable}>
            <TableHead>
              <TableRow>
                <TableHeader>Parcel</TableHeader>
                <TableHeader>Acres</TableHeader>
                <TableHeader>Over-Claiming Account</TableHeader>
                <TableHeader>Acct Recs</TableHeader>
                <TableHeader>Acct Σ NRI (%)</TableHeader>
                <TableHeader>Acct Σ WI (%)</TableHeader>
                <TableHeader>All Other Accounts With Holdings</TableHeader>
                <TableHeader>All Owners Σ NRI (%)</TableHeader>
                <TableHeader>All Owners Σ WI (%)</TableHeader>
                <TableHeader>Records</TableHeader>
                <TableHeader>Subtracts</TableHeader>
              </TableRow>
            </TableHead>
            <TableBody>
              {detail.parcels.map((parcel) => (
                <TableRow key={parcel.parcelKey}>
                  <TableCell>
                    <code>{parcel.parcelKey.slice(0, 8)}</code>
                    {parcel.overClaimed && (
                      <Tag type="red" size="sm">
                        over-claimed
                      </Tag>
                    )}
                  </TableCell>
                  <TableCell>{acres(parcel.parcelAcres)}</TableCell>
                  <TableCell>{parcel.worstAccount ?? '--'}</TableCell>
                  <TableCell>{count(parcel.worstAccountRecords)}</TableCell>
                  <TableCell>
                    {interestPercent(parcel.worstAccountNri)}
                  </TableCell>
                  <TableCell>
                    {interestPercent(parcel.worstAccountWi)}
                  </TableCell>
                  <TableCell>
                    {parcel.accounts
                      .filter((account) => account !== parcel.worstAccount)
                      .join(', ') || '--'}
                  </TableCell>
                  <TableCell>{interestPercent(parcel.sumNri)}</TableCell>
                  <TableCell>{interestPercent(parcel.sumWi)}</TableCell>
                  <TableCell>{count(parcel.recordCount)}</TableCell>
                  <TableCell>
                    {parcel.subtractNames.join(', ') || '--'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </>
      )}

      <div className={styles.detailSectionTitle}>Estate breakdown</div>
      <Table size="sm" className={styles.estateTable}>
        <TableHead>
          <TableRow>
            <TableHeader>Estate</TableHeader>
            <TableHeader>Net Acreage Units</TableHeader>
            <TableHeader>Calculations</TableHeader>
            <TableHeader>Σ NRI (%)</TableHeader>
            <TableHeader>Σ WI (%)</TableHeader>
          </TableRow>
        </TableHead>
        <TableBody>
          {detail.estateSummary.map((group) => (
            <TableRow key={`${group.estate}-${group.netAcreageUnits}`}>
              <TableCell>{group.estate ?? '--'}</TableCell>
              <TableCell>{group.netAcreageUnits ?? '--'}</TableCell>
              <TableCell>{count(group.rowCount)}</TableCell>
              <TableCell>{interestPercent(group.sumNri)}</TableCell>
              <TableCell>{interestPercent(group.sumWi)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <div className={styles.detailSectionTitle}>
        {`Contributing subtracts (${detail.contributions.length})`}
      </div>
      <DataTable
        data={detail.contributions}
        columns={columns as ColumnDef<SlotContribution>[]}
        getRowId={(row) => `${row.id}`}
        persistenceKey="admin-over-allocated-slot-contributions"
        scroll={{ mode: 'paginated' }}
        {...TABLE_CONFIG}
      />
    </div>
  );
};
